import React, { useState } from "react";
import PropTypes from "prop-types";

const StatusBadge = ({ status }) => {
  const statusConfig = {
    open: { bg: "bg-blue-100", text: "text-blue-800" },
    closed: { bg: "bg-green-100", text: "text-green-800" },
    pending: { bg: "bg-orange-100", text: "text-orange-800" },
    default: { bg: "bg-gray-100", text: "text-gray-800" }
  };

  const { bg, text } = statusConfig[status.toLowerCase()] || statusConfig.default;

  return (
    <span className={`text-xs px-2 py-1 rounded-full ${bg} ${text}`}>
      {status}
    </span>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired
};

const MessageBubble = ({ message, isUser }) => {
  return (
    <div
      className={`max-w-[80%] rounded-lg p-3 ${
        isUser
          ? "bg-blue-500 text-white rounded-br-none"
          : "bg-white border border-gray-200 rounded-bl-none"
      }`}
    >
      <p className="text-sm">{message.text}</p>
      <p
        className={`text-xs mt-1 ${
          isUser ? "text-blue-100" : "text-gray-500"
        }`}
      >
        {isUser ? "You" : "Support"} • Just now
      </p>
    </div>
  );
};

MessageBubble.propTypes = {
  message: PropTypes.object.isRequired,
  isUser: PropTypes.bool.isRequired
};

const TicketListItem = ({ ticket, isSelected, onClick }) => {
  return (
    <div
      onClick={() => onClick(ticket)}
      className={`p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors relative ${
        isSelected ? 'bg-blue-50' : ''
      }`}
    >
      {ticket.status === 'open' && (
        <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500"></div>
      )}
      
      <div className="flex justify-between items-start pl-3">
        <div className="flex items-center space-x-2">
          <h3 className="font-medium text-gray-800">{ticket.title}</h3>
        </div>
        <StatusBadge status={ticket.status} />
      </div>
      <p className="text-xs text-gray-500 mt-2 line-clamp-1 pl-3">
        {ticket.messages[0].text}
      </p>
      <div className="flex justify-between items-center mt-2 pl-3">
        <p className="text-xs text-gray-400">Ticket #{ticket.id}</p>
        <div className="flex items-center space-x-2">
          <p className="text-xs text-gray-400">
            {ticket.messages.length} {ticket.messages.length === 1 ? "message" : "messages"}
          </p>
        </div>
      </div>
    </div>
  );
};

TicketListItem.propTypes = {
  ticket: PropTypes.object.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

const Sports = () => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [messageInput, setMessageInput] = useState(""); 
  const [tickets, setTickets] = useState([
    {
      id: 1,
      title: "Login issue",
      status: "open",
      messages: [
        { from: "user", text: "I can't log in to my account." },
        { from: "support", text: "Have you tried resetting your password?" }
      ]
    },
    {
      id: 2,
      title: "Payment not received",
      status: "closed",
      messages: [
        { from: "user", text: "My payment hasn't arrived yet." },
        { from: "support", text: "We've processed your refund. It should appear in 3-5 business days." }
      ]
    },
    {
      id: 3,
      title: "Order delayed",
      status: "open",
      messages: [
        { from: "user", text: "My order is 10 days overdue. What's going on?" },
        { from: "support", text: "We're experiencing shipping delays. Let me check the status for you." },
        { from: "user", text: "This is unacceptable. I need this item by Friday!" },
        { from: "support", text: "I completely understand your frustration. Let me escalate this to our shipping team." },
        { from: "user", text: "Thank you, please keep me updated." }
      ]
    }
  ]);

  const openTicketsCount = tickets.filter(t => t.status === "open").length;
  const closedTicketsCount = tickets.filter(t => t.status === "closed").length;

  const handleTicketClick = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
    setMessageInput(""); 
  };

  const handleSendMessage = () => {
    if (!selectedTicket || !messageInput.trim()) return;

    const updatedTickets = tickets.map(ticket => 
      ticket.id === selectedTicket.id
        ? {
            ...ticket,
            messages: [
              ...ticket.messages,
              { from: "user", text: messageInput }
            ]
          }
        : ticket
    );

    setTickets(updatedTickets);
    setSelectedTicket(updatedTickets.find(t => t.id === selectedTicket.id));
    setMessageInput(""); 
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Support Center</h1>
          <div className="flex items-center space-x-2">
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
              {openTicketsCount} {openTicketsCount === 1 ? "ticket" : "tickets"} open
            </span>
            <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
              {closedTicketsCount} {closedTicketsCount === 1 ? "ticket" : "tickets"} closed
            </span>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-full">
          {tickets.map((ticket) => (
            <TicketListItem
              key={`ticket-${ticket.id}`}
              ticket={ticket}
              isSelected={selectedTicket?.id === ticket.id}
              onClick={handleTicketClick}
            />
          ))}
        </div>
      </div>

      {isModalOpen && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[70vh] flex flex-col">
            <div className="border-b border-gray-200 p-4 flex justify-between items-center">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {selectedTicket.title}
                </h2>
                <div className="flex items-center mt-1 space-x-2">
                  <StatusBadge status={selectedTicket.status} />
                  <span className="text-xs text-gray-500">
                    Ticket #{selectedTicket.id}
                  </span>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="flex-1 p-4 overflow-y-auto bg-gray-50 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              <div className="space-y-4">
                {selectedTicket.messages.map((message, idx) => (
                  <div
                    key={`msg-${idx}`}
                    className={`flex ${
                      message.from === "user" ? "justify-end" : "justify-start"
                    }`}
                  >
                    <MessageBubble
                      message={message}
                      isUser={message.from === "user"}
                    />
                  </div>
                ))}
                <div ref={(el) => el?.scrollIntoView({ behavior: "smooth" })} />
              </div>
            </div>

            <div className="border-t border-gray-200 p-4 bg-white">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type your message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && messageInput.trim()) {
                      handleSendMessage();
                    }
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  aria-label="Type your message"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!messageInput.trim()}
                  className={`p-2 rounded-full ${
                    messageInput.trim()
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-300 cursor-not-allowed"
                  } text-white transition-colors`}
                  aria-label="Send message"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sports;